/**
 * A notification when something new lands in the inbox and asks for the person — finished, needs
 * them, failed — never for each tool or for what was already there when the app opened
 * (docs/refactor/03-caixa-de-entrada.md). Native in the desktop app; the browser's, when allowed, on
 * the web.
 */

import { useEffect, useRef } from 'react'
import { desktopNotify, isDesktop } from './desktop'
import { useI18n } from './i18n'
import type { Inbox, InboxItem } from './types'

/** The items to tell the person about: unread, asking for them, not seen by this app before. */
export function newsSince(seen: ReadonlySet<string>, items: InboxItem[]): InboxItem[] {
  return items.filter((item) => !item.read && item.kind !== 'waiting' && !seen.has(item.key))
}

/** Fired on window for every notification sent, with its title and body: what a test can watch. */
export const NOTIFIED_EVENT = 'devpulse:notified'

export function notify(title: string, body: string) {
  window.dispatchEvent(new CustomEvent(NOTIFIED_EVENT, { detail: { title, body } }))
  if (isDesktop()) {
    void desktopNotify(title, body).catch(() => {})
    return
  }
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') new Notification(title, { body, icon: '/favicon.png' })
  } catch {
    // A browser that refuses notifications here: the inbox still has it.
  }
}

/** Whether the browser can be asked for notifications (the desktop app needs no asking). */
export function canAskForNotifications(): boolean {
  return !isDesktop() && typeof Notification !== 'undefined' && Notification.permission === 'default'
}

export function useInboxNotifications(inbox: Inbox | undefined) {
  const { t } = useI18n()
  const seen = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (!inbox) return
    // What is there when the app opens is not news.
    if (seen.current === null) {
      seen.current = new Set(inbox.items.map((i) => i.key))
      return
    }
    for (const item of newsSince(seen.current, inbox.items)) {
      notify(t(`inbox.notify.${item.kind}`), item.title)
      seen.current.add(item.key)
    }
  }, [inbox, t])
}

/** A run here this long, ending while the window is not in front, is worth a notification. */
export const LONG_RUN_MS = 60_000

/**
 * A turn run on this machine ended (docs/refactor/03-caixa-de-entrada.md): when it was long and the
 * person is not looking at the app, the system tells them — the inbox's way, for what never went
 * through the server's queue. Stopped turns (■) are the person's own doing: nothing to tell.
 */
export function shouldNotifyRunEnded(startedAt: number, endedAt: number, code: number | null, away: boolean): boolean {
  return away && code !== null && endedAt - startedAt >= LONG_RUN_MS
}

export function appIsAway(): boolean {
  try {
    return document.hidden || !document.hasFocus()
  } catch {
    return false
  }
}
